import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Typography,
    Tooltip,
    TablePagination,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Sync as SyncIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { faqsService, FAQ } from '../../services/faqsService';
import { useSnackbar } from '../../hooks/useSnackbar';
import ConfirmDialog from '../../components/ConfirmDialog';

export const FAQManagementTab: React.FC = () => {
    const [faqs, setFaqs] = useState<FAQ[]>([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
    const [formData, setFormData] = useState({ question: '', answer: '', is_active: true });
    const { showSuccess, showError } = useSnackbar();

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        content: '',
        onConfirm: () => { }
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchFaqs = async () => {
        try {
            setLoading(true);
            const res = await faqsService.getAll({
                page: page + 1,
                limit: rowsPerPage,
                search: searchQuery
            });
            if (res && res.data) {
                setFaqs(res.data);
                setTotalRows(res.total);
            } else {
                // Fallback for old API structure if needed, or init empty
                setFaqs([]);
                setTotalRows(0);
            }
        } catch (error) {
            console.error(error);
            showError('Không thể tải danh sách câu hỏi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchFaqs();
        }, 500); // Debounce search
        return () => clearTimeout(timer);
    }, [page, rowsPerPage, searchQuery]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // ... (handlers remain mostly same, but need to refetch correctly) ...

    const handleOpen = (faq?: FAQ) => {
        if (faq) {
            setEditingFaq(faq);
            setFormData({ question: faq.question, answer: faq.answer, is_active: faq.is_active });
        } else {
            setEditingFaq(null);
            setFormData({ question: '', answer: '', is_active: true });
        }
        setOpenModal(true);
    };

    const handleClose = () => {
        setOpenModal(false);
        setEditingFaq(null);
    };

    const handleSubmit = async () => {
        try {
            if (editingFaq) {
                await faqsService.update(editingFaq.id, formData);
                showSuccess('Cập nhật thành công');
            } else {
                await faqsService.create(formData);
                showSuccess('Thêm mới thành công');
            }
            handleClose();
            fetchFaqs();
        } catch (error) {
            showError('Có lỗi xảy ra');
        }
    };

    const handleDeleteClick = (id: string) => {
        setConfirmDialog({
            open: true,
            title: 'Xác nhận xóa',
            content: 'Bạn có chắc chắn muốn xóa câu hỏi này không? Hành động này không thể hoàn tác.',
            onConfirm: () => handleDelete(id)
        });
    };

    const handleDelete = async (id: string) => {
        try {
            await faqsService.delete(id);
            showSuccess('Đã xóa câu hỏi');
            fetchFaqs();
        } catch (error) {
            showError('Xóa thất bại');
        } finally {
            setConfirmDialog({ ...confirmDialog, open: false });
        }
    };

    const handleSync = async () => {
        try {
            setLoading(true);
            await faqsService.sync();
            showSuccess('Đã đồng bộ dữ liệu vào trí tuệ nhân tạo!');
        } catch (error) {
            showError('Đồng bộ thất bại');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Danh sách câu hỏi mẫu ({totalRows})</Typography>
                <Box>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        style={{ display: 'none' }}
                        id="import-faq-file"
                        onChange={async (e) => {
                            if (e.target.files && e.target.files[0]) {
                                try {
                                    setLoading(true);
                                    const res = await faqsService.importFaqs(e.target.files[0]);
                                    if (res.failedImports > 0) {
                                        showError(`Import hoàn tất: ${res.successfulImports} thành công, ${res.failedImports} thất bại.`);
                                        console.error(res.errors);
                                    } else {
                                        showSuccess(`Import thành công ${res.successfulImports} câu hỏi.`);
                                    }
                                    fetchFaqs();
                                } catch (err: any) {
                                    showError(err.message || 'Import thất bại');
                                } finally {
                                    setLoading(false);
                                    e.target.value = ''; // Reset input
                                }
                            }
                        }}
                    />
                    <Button
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => faqsService.downloadTemplate()}
                    >
                        Tải mẫu
                    </Button>
                    <label htmlFor="import-faq-file">
                        <Button
                            variant="outlined"
                            component="span"
                            sx={{ mr: 1 }}
                            disabled={loading}
                        >
                            Import Excel
                        </Button>
                    </label>
                    <Button
                        variant="outlined"
                        startIcon={<SyncIcon />}
                        onClick={handleSync}
                        sx={{ mr: 1 }}
                        disabled={loading}
                    >
                        Cập nhật Tri thức
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpen()}
                    >
                        Thêm câu hỏi
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Tìm kiếm câu hỏi hoặc câu trả lời..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0); // Reset page on search
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                />
            </Box>


            <TableContainer component={Paper}>
                <Table sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell width="30%">Câu hỏi</TableCell>
                            <TableCell width="60%">Câu trả lời mẫu</TableCell>
                            <TableCell align="right" width="10%">Hành động</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={20} />
                                </TableCell>
                            </TableRow>
                        ) : faqs.map((faq) => (
                            <TableRow key={faq.id}>
                                <TableCell sx={{ wordWrap: 'break-word', verticalAlign: 'top' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        {faq.is_active ?
                                            <Tooltip title="Đang sử dụng">
                                                <ActiveIcon color="success" fontSize="small" sx={{ mt: 0.5, flexShrink: 0 }} />
                                            </Tooltip>
                                            :
                                            <Tooltip title="Ngừng sử dụng">
                                                <InactiveIcon color="disabled" fontSize="small" sx={{ mt: 0.5, flexShrink: 0 }} />
                                            </Tooltip>
                                        }
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {faq.question}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', verticalAlign: 'top' }}>
                                    {faq.answer}
                                </TableCell>
                                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Tooltip title="Sửa">
                                            <IconButton size="small" onClick={() => handleOpen(faq)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Xóa">
                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(faq.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && faqs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">Chưa có dữ liệu. Hãy thêm câu hỏi mẫu để dạy chatbot.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Số dòng mỗi trang:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
                />
            </TableContainer>

            <Dialog open={openModal} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>{editingFaq ? 'Sửa câu hỏi' : 'Thêm câu hỏi mới'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Câu hỏi (User query)"
                            fullWidth
                            value={formData.question}
                            onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                            placeholder="VD: Trường có bao nhiêu giáo viên?"
                        />
                        <TextField
                            label="Câu trả lời mẫu (Expected Answer)"
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.answer}
                            onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                            placeholder="VD: Trường có tổng cộng 28 giáo viên, bao gồm..."
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            }
                            label="Kích hoạt (Sử dụng cho RAG)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Hủy</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={!formData.question || !formData.answer}>
                        Lưu
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                content={confirmDialog.content}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
                confirmColor="error"
                confirmText="Xóa"
            />
        </Box >
    );
};
